import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Calendar, MessageSquare, Users, Check, Flame, X, UserPlus, UserCheck } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcon = {
  new_event: Calendar,
  new_message: MessageSquare,
  membership_request: Users,
  post_comment: MessageSquare,
  pitstop_request: Flame,
  network_chat: MessageSquare,
  pitstop_approved: Flame,
  pitstop_rejected: Flame,
  new_follower: Users,
  post_like: Bell,
  profile_incomplete: Bell,
  friend_request: UserPlus,
  friend_accepted: UserCheck,
};

const typeColor = {
  new_event: 'text-emerald-400',
  new_message: 'text-sky-400',
  membership_request: 'text-amber-400',
  post_comment: 'text-orange-400',
  pitstop_request: 'text-amber-400',
  network_chat: 'text-violet-400',
  pitstop_approved: 'text-green-400',
  pitstop_rejected: 'text-red-400',
  new_follower: 'text-sky-400',
  post_like: 'text-pink-400',
  profile_incomplete: 'text-amber-400',
  friend_request: 'text-sky-400',
  friend_accepted: 'text-emerald-400',
};

const typeRoute = {
  new_event: '/events',
  new_message: '/beskeder',
  membership_request: '/dashboard',
  post_comment: '/feed',
  pitstop_request: '/admin?tab=pitstops',
  accommodation_request: '/admin?tab=accommodations',
  network_chat: '/dashboard',
  pitstop_approved: '/kort',
  pitstop_rejected: '/kort',
  accommodation_approved: '/kort',
  accommodation_rejected: '/kort',
  new_follower: '/dashboard',
  post_like: '/feed',
  profile_incomplete: '/dashboard',
  friend_request: '/beskeder',
  friend_accepted: '/beskeder',
};

const timeAgo = (dateStr) => {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'Nu';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}t`;
  return `${Math.floor(diff / 86400)}d`;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingMemberships, setPendingMemberships] = useState({ count: 0, first_club_id: null, requests: [] });
  const panelRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchPendingMemberships = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/clubs/pending-requests-count`, { headers });
      setPendingMemberships(res.data);
    } catch {}
  };

  const fetchCount = async () => {
    try {
      const res = await axios.get(`${API_URL}/notifications/unread-count`, { headers });
      const count = res.data.count;
      setUnreadCount(count);
      // PWA badge
      if ('setAppBadge' in navigator) {
        count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge();
      }
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/notifications?limit=20`, { headers });
      setNotifications(res.data);
    } catch {}
  };

  useEffect(() => {
    if (!token) return;
    fetchCount();
    fetchPendingMemberships();
    const interval = setInterval(() => {
      fetchCount();
      fetchPendingMemberships();
    }, 30000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
      fetchPendingMemberships();
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (id) => {
    await axios.put(`${API_URL}/notifications/${id}/read`, {}, { headers }).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await axios.put(`${API_URL}/notifications/mark-all-read`, {}, { headers }).catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    if ('clearAppBadge' in navigator) navigator.clearAppBadge();
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    setOpen(false);
    const d = n.data || {};
    
    switch (n.type) {
      case 'new_event':
        // Navigate to the club that created the event
        if (d.club_id) {
          navigate(`/klubber/${d.club_id}`);
        } else {
          navigate('/events');
        }
        break;
      case 'new_follower':
        // Navigate to the club profile
        if (d.club_id) {
          navigate(`/klubber/${d.club_id}`);
        } else {
          navigate('/dashboard');
        }
        break;
      case 'profile_incomplete':
        // Navigate to the club profile for editing
        if (d.club_id) {
          navigate(`/klubber/${d.club_id}`);
        } else {
          navigate('/dashboard');
        }
        break;
      case 'membership_request':
        if (d.club_id) {
          navigate(`/klubber/${d.club_id}`);
        } else {
          navigate('/dashboard');
        }
        break;
      case 'pitstop_approved':
      case 'pitstop_rejected':
        if (d.pitstop_id) {
          navigate(`/pitstop/${d.pitstop_id}`);
        } else {
          navigate('/kort');
        }
        break;
      case 'pitstop_request':
        navigate('/admin');
        setTimeout(() => {
          const tabBtn = document.querySelector('[data-testid="tab-pitstops"]');
          if (tabBtn) tabBtn.click();
        }, 500);
        break;
      case 'post_like':
      case 'post_comment':
        navigate('/feed');
        break;
      case 'new_message':
      case 'friend_request':
      case 'friend_accepted':
        navigate('/beskeder');
        break;
      case 'ride_video_ready':
        // Deep-link straight to an auto-open share dialog — the rider just
        // needs ONE tap to get their Reel into WhatsApp/Instagram.
        if (d.ride_id) {
          navigate(`/mine-ture?share=${encodeURIComponent(d.ride_id)}`);
        } else {
          navigate('/mine-ture');
        }
        break;
      default:
        navigate(typeRoute[n.type] || '/');
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1.5 rounded-full hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-4.5 h-4.5 text-white/70" />
        {(unreadCount + pendingMemberships.count) > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1" data-testid="notification-badge">
            {(unreadCount + pendingMemberships.count) > 99 ? '99+' : (unreadCount + pendingMemberships.count)}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile overlay */}
          <div className="fixed inset-0 bg-black/40 z-40 sm:hidden" onClick={() => setOpen(false)} />
          
          <div className="fixed inset-x-3 top-14 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 max-h-[70vh] sm:max-h-[400px] bg-zinc-900/98 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="notification-panel">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="text-sm font-semibold text-white">{t("notifications.title")}</span>
              <div className="flex items-center gap-3">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                    data-testid="mark-all-read-btn"
                  >
                    <Check className="w-3 h-3" />
                    {t("notifications.markAllRead")}
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="p-1 rounded-full hover:bg-white/10 transition-colors"
                  data-testid="notification-close-btn"
                >
                  <X className="w-4 h-4 text-white/60" />
                </button>
              </div>
            </div>

          {/* Notification list */}
          <div className="overflow-y-auto max-h-[340px]">

            {/* ── STICKY: Pending membership requests (persistent until handled) ── */}
            {pendingMemberships.count > 0 && (
              <button
                onClick={() => {
                  setOpen(false);
                  if (pendingMemberships.first_club_id) {
                    navigate(`/klubber/${pendingMemberships.first_club_id}`);
                  }
                }}
                className="w-full text-left px-4 py-3 flex items-start gap-3 bg-amber-500/10 border-b border-amber-500/20 hover:bg-amber-500/15 transition-colors"
                data-testid="pending-memberships-sticky"
              >
                <div className="mt-0.5 w-8 h-8 rounded-full bg-amber-500/25 flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-amber-300">
                    {pendingMemberships.count} {pendingMemberships.count === 1 ? 'person venter på godkendelse' : 'personer venter på godkendelse'}
                  </div>
                  <div className="text-xs text-amber-400/60 mt-0.5">
                    {pendingMemberships.requests.slice(0, 2).map(r => r.user_name || r.user_nickname || 'Ukendt').join(', ')}
                    {pendingMemberships.requests.length > 2 ? ` +${pendingMemberships.requests.length - 2}` : ''}
                  </div>
                  <div className="text-[10px] text-amber-500/50 mt-1">Tryk for at godkende eller afvise</div>
                </div>
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0 mt-1" />
              </button>
            )}

            {notifications.length === 0 && pendingMemberships.count === 0 ? (
              <div className="py-10 text-center text-white/40 text-sm">
                {t("notifications.noNotifications")}
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = typeIcon[n.type] || Bell;
                const color = typeColor[n.type] || 'text-white/60';
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-white/5 transition-colors border-b border-white/5 ${!n.read ? 'bg-white/[0.03]' : ''}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${!n.read ? 'bg-emerald-400/15' : 'bg-white/5'}`}>
                      <Icon className={`w-4 h-4 ${!n.read ? color : 'text-white/30'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-medium truncate ${!n.read ? 'text-white' : 'text-white/50'}`}>
                        {n.title}
                      </div>
                      <div className={`text-xs truncate mt-0.5 ${!n.read ? 'text-white/60' : 'text-white/30'}`}>
                        {n.body}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-[10px] text-white/30">{timeAgo(n.created_at)}</span>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
